import { createContext, useContext } from 'react';
import { storageKey } from '../config';
import type { Principal } from './principal';

export interface AuthContextValue {
  token: string | null;
  principal: Principal | null;
  isAuthenticated: boolean;
  /** False until the native token store has been consulted (browser: immediately true). */
  bootstrapped: boolean;
  /** Stores the token; the principal is derived from it. */
  signIn: (token: string, expiresAt: Date) => void;
  /** Clears the token and cached data; best-effort POST /auth/logout. */
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue>({
  token: null,
  principal: null,
  isAuthenticated: false,
  bootstrapped: true,
  signIn: () => undefined,
  signOut: () => Promise.resolve()
});

export const useAuth = (): AuthContextValue => useContext(AuthContext);

/**
 * The SSR set an `InviteOperationsApp` marker cookie when the native app initialised; an
 * unauthenticated request then bounced to /operations/app/init instead of the login form.
 */
const appMarkerKey = (): string => storageKey('appCookie');

export const hasAppMarker = (): boolean => {
  try {
    return localStorage.getItem(appMarkerKey()) === '1';
  } catch {
    return false;
  }
};

export const setAppMarker = (value: boolean): void => {
  try {
    if (value) {
      localStorage.setItem(appMarkerKey(), '1');
    } else {
      localStorage.removeItem(appMarkerKey());
    }
  } catch {
    // ignore
  }
};
